import { useCallback, useRef, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import useSetInterval from "@entrilia/shared/hooks/useSetInterval";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../api/adminApi";

const getObjectEtag = withErrorHandling(adminApi.getObjectEtag);

const POLL_INTERVAL_MS = 3_000;

const FORCE_POLL_TIMEOUT_MS = 60_000;

const MAX_FORCED_POLLS = Math.ceil(FORCE_POLL_TIMEOUT_MS / POLL_INTERVAL_MS);

interface Params {
  objectType: string;
  objectId: string;
  pendingUpdate: boolean;
  onChange: () => void;
}

/**
 * Polls an object's etag and calls `onChange` when it moves, so the caller can re-fetch.
 * Polls automatically while `pendingUpdate` is true. `forcePoll` opens a bounded polling window even
 * when `pendingUpdate` is still false — for actions whose flag arrives asynchronously (e.g. a document
 * delete, where the response returns before server-side cleanup starts).
 */
const useObjectChangePolling = ({ objectType, objectId, pendingUpdate, onChange }: Params) => {
  const etagRef = useRef<string | undefined>(undefined);
  const [forcedPollsLeft, setForcedPollsLeft] = useState(0);

  const poll = useCallback(async () => {
    setForcedPollsLeft((left) => Math.max(0, left - 1));

    const [etag, error] = await getObjectEtag(objectType, objectId);
    if (error) {
      logError(error, "[useObjectChangePolling] getObjectEtag");
      return;
    }

    const previousEtag = etagRef.current;
    etagRef.current = etag;
    const isInitialPoll = previousEtag === undefined;
    const etagMoved = !isInitialPoll && etag !== previousEtag;

    if (isInitialPoll || etagMoved) {
      onChange();
    }

    // A moved etag means the async effect landed — end the force window and let `pendingUpdate` take over.
    if (etagMoved) {
      setForcedPollsLeft(0);
    }
  }, [objectType, objectId, onChange]);

  useSetInterval(poll, POLL_INTERVAL_MS, { disabled: forcedPollsLeft === 0 && !pendingUpdate });

  const forcePoll = useCallback(() => {
    setForcedPollsLeft(MAX_FORCED_POLLS);
  }, []);

  return { forcePoll };
};

export default useObjectChangePolling;
